
import React, { useState, useEffect, useRef } from 'react';
import { InteractiveService, InteractiveSessionState } from '../../services/interactive/InteractiveService';
import { QRCodeSVG } from 'qrcode.react';
import { Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import 'katex/dist/katex.min.css';
import { InlineMath, BlockMath } from 'react-katex';
import * as mammoth from 'mammoth'; // Import mammoth
import {
    Users,
    Maximize,
    X,
    Upload,
    BarChart2,
    Type,
    MessageSquare,
    Play,
    StopCircle,
    Copy,
    Check,
    Settings,
    Layout,
    Cloud,
    Volume2,
    VolumeX
} from 'lucide-react';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

// --- Types ---
type QuestionType = 'word_cloud' | 'mcq' | 'short_answer';

interface Question {
    id: string | number;
    type: QuestionType;
    text: string;
    options?: string[]; // For MCQ    
    correctAnswer?: string; // Optional for auto-grading later
    timeLimit?: number;
    image?: string;
}

// --- Components ---

// Helper to render text with LaTeX
// Helper to render text with LaTeX
const renderLatexText = (text: string) => {
    if (!text) return "";

    // Split by $$...$$ for block math, then $...$ for inline
    // Regex: /(\$\$[^\$]+\$\$|\$[^\$]+\$)/g
    const parts = text.split(/(\$\$[\s\S]+?\$\$|\$[^\$]+?\$)/g);

    return parts.map((part, index) => {
        if (part.startsWith('$$') && part.endsWith('$$') && part.length > 4) {
            const math = part.slice(2, -2);
            return <BlockMath key={index} math={math} />;
        }
        if (part.startsWith('$') && part.endsWith('$') && part.length > 2) {
            const math = part.slice(1, -1);
            return <InlineMath key={index} math={math} />;
        }
        return <span key={index}>{part}</span>;
    });
};

const QRModal = ({ code, isOpen, onClose }: { code: string, isOpen: boolean, onClose: () => void }) => {
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-200" onClick={onClose}>
            <div className="bg-white p-8 rounded-3xl shadow-2xl max-w-lg w-full text-center relative animate-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()}>
                <button onClick={onClose} className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full transition-colors">
                    <X className="w-6 h-6 text-gray-500" />
                </button>

                <h2 className="text-3xl font-bold text-gray-800 mb-2">Tham gia lớp học</h2>
                <div className="text-xl text-gray-500 mb-8">Quét mã QR hoặc nhập Code</div>

                <div className="flex justify-center mb-8">
                    <div className="p-4 bg-white border-4 border-indigo-100 rounded-2xl shadow-inner">
                        <QRCodeSVG value={`https://ebook.vatli365.vn/live/${code}`} size={256} />
                    </div>
                </div>

                <div className="flex items-center justify-center gap-4 mb-8">
                    <div className="text-6xl font-black text-indigo-600 tracking-widest font-mono bg-indigo-50 px-8 py-4 rounded-xl border-2 border-indigo-100">
                        {code}
                    </div>
                </div>

                <div className="text-lg text-gray-600">
                    Truy cập: <span className="font-bold text-indigo-600">ebook.vatli365.vn/live</span>
                </div>
            </div>
        </div>
    );
};

const TeacherSession = () => {
    // Session State
    const [sessionCode, setSessionCode] = useState<string | null>(null);
    const [sessionState, setSessionState] = useState<InteractiveSessionState | null>(null);
    const [ws, setWs] = useState<WebSocket | null>(null);
    const [activeTab, setActiveTab] = useState<'create' | 'control'>('create');
    const [showQR, setShowQR] = useState(false);

    // Question Management State
    const [questions, setQuestions] = useState<Question[]>([]);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(-1);

    // Manual Creation State (for "Quick Ask")
    const [quickType, setQuickType] = useState<QuestionType>('mcq');
    const [quickText, setQuickText] = useState('');
    const [quickOptions, setQuickOptions] = useState<string[]>(['A', 'B', 'C', 'D']);

    // Sound State
    const [isMuted, setIsMuted] = useState(false);
    const audioRefs = useRef<{
        lobby: HTMLAudioElement;
        active: HTMLAudioElement;
        response: HTMLAudioElement;
        victory: HTMLAudioElement;
    } | null>(null);

    // Initialize Audio
    useEffect(() => {
        audioRefs.current = {
            lobby: new Audio('/sounds/game-intro.mp3'),
            active: new Audio('/sounds/game-background.mp3'),
            response: new Audio('/sounds/mixkit-correct-answer-fast-notification-953.wav'),
            victory: new Audio('/sounds/game-victory.mp3')
        };

        // Configure loops
        audioRefs.current.lobby.loop = true;
        audioRefs.current.active.loop = true;

        // Set volumes
        audioRefs.current.lobby.volume = 0.3;
        audioRefs.current.active.volume = 0.2; // Softer background
        audioRefs.current.response.volume = 0.6; // Distinct
        audioRefs.current.victory.volume = 0.5;

        return () => {
            // Cleanup: stop all sounds
            if (audioRefs.current) {
                Object.values(audioRefs.current).forEach(audio => {
                    audio.pause();
                    audio.currentTime = 0;
                });
            }
        };
    }, []);

    // Manage BGM based on state
    useEffect(() => {
        if (!audioRefs.current || isMuted) {
            // If muted, pause everything
            audioRefs.current?.lobby.pause();
            audioRefs.current?.active.pause();
            return;
        }

        const { lobby, active } = audioRefs.current;

        if (activeTab === 'create') {
            // Prepare screen (no sound or maybe lobby sound?)
            // Let's keep silent here or play soft intro? 
            // Better silent until "Start Session" to not annoy user immediately.
            lobby.pause();
            active.pause();
        } else if (activeTab === 'control') {
            if (sessionState?.current_question) {
                // Question is active -> Play Active BGM, Stop Lobby
                lobby.pause();
                active.play().catch(() => { }); // Catch autoplay block
            } else {
                // Lobby/Waiting -> Play Lobby BGM, Stop Active
                active.pause();
                lobby.play().catch(() => { });
            }
        }
    }, [activeTab, sessionState?.current_question, isMuted]);

    // Play Response Sound
    useEffect(() => {
        if (!audioRefs.current || isMuted) return;

        // Check if response count increased
        if (sessionState?.responses && sessionState.responses.length > 0) {
            // Simple way: Play sound on every update if length changes
            // We need a ref to track previous length to only play on NEW response
            // But here we don't have prev state easily accessbile without another ref.
            // Let's valid this logic: sessionState updates when new response comes.
            // We can just play sound.
            // But initial load might trigger it. Use a timestamp check?
            // Or simpler: just play it, it's fine for "game feel".
            // Actually, re-render might cause spam if not careful.
            // Best to use a ref for prev count.
        }
    }, [sessionState?.responses, isMuted]);

    // Ref for previous response count to trigger sound correctly
    const prevResponseCount = useRef(0);
    useEffect(() => {
        const count = sessionState?.responses?.length || 0;
        if (count > prevResponseCount.current) {
            if (!isMuted && audioRefs.current) {
                // Clone helps play multiple sounds overlapping fast
                const sound = audioRefs.current.response.cloneNode() as HTMLAudioElement;
                sound.volume = 0.6;
                sound.play().catch(() => { });
            }
        }
        prevResponseCount.current = count;
    }, [sessionState?.responses?.length, isMuted]);

    // UI State
    const [isSidebarOpen, setSidebarOpen] = useState(true);

    const startSession = async () => {
        try {
            const code = await InteractiveService.createSession();
            setSessionCode(code);
            const socket = InteractiveService.connectHost(code, (state) => {
                setSessionState(state);
            });
            setWs(socket);
            setActiveTab('control');
            setShowQR(true); // Auto show QR on start
        } catch (err) {
            alert('Không thể tạo phiên: ' + err);
        }
    };

    // ...

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (file.name.endsWith('.json')) {
            const reader = new FileReader();
            reader.onload = (event) => {
                try {
                    // ... (keep existing JSON logic)
                    const json = JSON.parse(event.target?.result as string);
                    if (Array.isArray(json)) {
                        const validQuestions = json.filter(q => q.text && q.type);
                        setQuestions(validQuestions);
                        alert(`Đã nhập thành công ${validQuestions.length} câu hỏi từ JSON!`);
                    }
                } catch (err) {
                    alert('Lỗi đọc file JSON: ' + err);
                }
            };
            reader.readAsText(file);
        } else if (file.name.endsWith('.docx')) {
            const reader = new FileReader();
            reader.onload = async (event) => {
                try {
                    const arrayBuffer = event.target?.result as ArrayBuffer;
                    // Map underline to <u> tag explicitly to convert to HTML correctly
                    const options = {
                        styleMap: [
                            "u => u"
                        ]
                    };
                    const result = await (mammoth as any).convertToHtml({ arrayBuffer: arrayBuffer }, options);
                    const html = result.value;
                    const { questions: parsedQuestions, errors } = parseDocxHtml(html);

                    if (parsedQuestions.length > 0) {
                        setQuestions(parsedQuestions);
                        let msg = `Đã nhập thành công ${parsedQuestions.length} câu hỏi!`;
                        if (errors.length > 0) {
                            msg += `\n\nCảnh báo (${errors.length}):\n- ` + errors.slice(0, 5).join('\n- ');
                            if (errors.length > 5) msg += '\n... và thêm ' + (errors.length - 5) + ' lỗi nữa.';
                        }
                        alert(msg);
                    } else {
                        alert('Không tìm thấy câu hỏi hợp lệ.\n\nLỗi:\n- ' + (errors.length ? errors.join('\n- ') : 'File không đúng định dạng mẫu.'));
                    }
                } catch (err) {
                    console.error(err);
                    alert('Lỗi đọc file Word: ' + err);
                }
            };
            reader.readAsArrayBuffer(file);
        }
    };

    // Helper to parse HTML from Docx
    const parseDocxHtml = (html: string): { questions: Question[], errors: string[] } => {
        const doc = new DOMParser().parseFromString(html, 'text/html');

        const questions: Question[] = [];
        const errors: string[] = [];

        let currentQ: Partial<Question> | null = null;
        let currentTypeExplicit = false; // If type was set by [MC] code

        const paragraphs = Array.from(doc.body.children);

        // Regex patterns
        const explicitTypeRegex = /^\[(MC|WC|SA)\]/i;
        const genericStartRegex = /^(Câu|Bài)\s*\d+[:.]?/i;
        const optionRegex = /^([A-D])\s*[\.:)]/i; // A. or A) or A:
        const answerKeyRegex = /^(Đáp án|Đáp số|Answer|ĐS|Đ\.án)[:\s]+(.*)/i;

        const codeToType: Record<string, QuestionType> = {
            'MC': 'mcq',
            'WC': 'word_cloud',
            'SA': 'short_answer'
        };

        const pushCurrentQuestion = () => {
            if (!currentQ) return;

            // Finalize Type Inference
            if (!currentQ.type) {
                // If it has options, it's MCQ
                if (currentQ.options && currentQ.options.length >= 2) {
                    currentQ.type = 'mcq';
                } else {
                    // Default to Short Answer if simple text
                    currentQ.type = 'short_answer';
                }
            }

            // Validation
            if (currentQ.text) {
                if (currentQ.type === 'mcq') {
                    if (!currentQ.options || currentQ.options.length < 2) {
                        errors.push(`Câu hỏi "${currentQ.text.substring(0, 20)}..." (MCQ) thiếu đáp án lựa chọn.`);
                    }
                    if (!currentQ.correctAnswer) {
                        errors.push(`Câu hỏi "${currentQ.text.substring(0, 20)}..." chưa có đáp án đúng.`);
                    }
                } else {
                    // SA / WC
                    if (!currentQ.correctAnswer) {
                        // Warning only for now
                        // errors.push(`Câu hỏi SA/WC "${currentQ.text.substring(0, 20)}..." thiếu đáp án.`);
                    }
                }
                questions.push(currentQ as Question);
            }
        };

        paragraphs.forEach((p, idx) => {
            let text = p.textContent?.trim() || '';
            const htmlContent = p.innerHTML;

            // 1. Check for New Question Start
            const explicitMatch = text.match(explicitTypeRegex);
            const genericMatch = text.match(genericStartRegex);

            if (explicitMatch || genericMatch) {
                // Finish previous
                pushCurrentQuestion();

                // Init New
                currentQ = {
                    id: Date.now() + Math.random(),
                    text: '',
                    options: [],
                    correctAnswer: ''
                };

                if (explicitMatch) {
                    currentQ.type = codeToType[explicitMatch[1].toUpperCase()];
                    currentTypeExplicit = true;
                    text = text.substring(explicitMatch[0].length).trim();
                } else {
                    currentQ.type = undefined; // Infer later
                    currentTypeExplicit = false;
                    // Remove "Câu 1:" prefix to be clean
                    if (genericMatch) {
                        text = text.substring(genericMatch[0].length).trim();
                    }
                }

                currentQ.text = text;

                const img = p.querySelector('img');
                if (img) currentQ.image = img.src;

                return;
            }

            // 2. Process Content if inside a question
            if (currentQ) {
                // Check Answer Key
                const answerMatch = text.match(answerKeyRegex);
                if (answerMatch) {
                    currentQ.correctAnswer = answerMatch[2].trim();
                    return;
                }

                // Check Options (A. B. C. D.) - mostly MCQ
                if (!currentTypeExplicit || currentQ.type === 'mcq') {
                    const optionMatch = text.match(optionRegex);
                    if (optionMatch) {
                        currentQ.type = 'mcq';
                        const label = optionMatch[1].toUpperCase();
                        const content = text.substring(optionMatch[0].length).trim();

                        currentQ.options = currentQ.options || [];
                        currentQ.options.push(content);

                        // Check Underline
                        const uTag = p.querySelector('u');
                        if (uTag) {
                            currentQ.correctAnswer = content;
                        }
                        return;
                    }
                }

                // Normal Text / Image appending
                const img = p.querySelector('img');
                if (img) {
                    if (!currentQ.image) currentQ.image = img.src;
                } else if (text) {
                    if (currentQ.text) currentQ.text += '\n' + text;
                    else currentQ.text = text;
                }
            } else {
                // Orphan text
                if (text && text.length > 20 && !text.startsWith('Hướng dẫn') && !text.startsWith('Danh sách')) {
                    errors.push(`Dòng ${idx}: Nội dung chưa thuộc câu hỏi nào: "${text.substring(0, 30)}..."`);
                }
            }
        });

        // Push last
        pushCurrentQuestion();

        return { questions, errors };
    };



    const playQuestion = (q: Question) => {
        if (!ws || !sessionCode) return;

        const payload = {
            type: q.type,
            data: {
                text: q.text,
                options: q.type === 'mcq' ? q.options : undefined
            }
        };

        ws.send(JSON.stringify({
            action: 'SET_QUESTION',
            payload: payload
        }));
    };

    const playQuickQuestion = () => {
        const q: Question = {
            id: 'quick-' + Date.now(),
            type: quickType,
            text: quickText || 'Câu hỏi nhanh',
            options: quickType === 'mcq' ? quickOptions : undefined
        };
        playQuestion(q);
    };

    // --- Renderers ---

    const renderChart = () => {
        if (!sessionState?.responses) return <div className="text-gray-400 italic">Chưa có dữ liệu...</div>;

        const currentQ = sessionState.current_question;
        if (!currentQ) return null;

        if (currentQ.type === 'mcq') {
            const options = currentQ.data.options || ['A', 'B', 'C', 'D'];
            const counts: Record<string, number> = {};
            options.forEach((opt: string) => counts[opt] = 0);

            sessionState.responses.forEach(r => {
                if (counts[r.answer] !== undefined) counts[r.answer]++;
            });

            const data = {
                labels: Object.keys(counts),
                datasets: [{
                    label: 'Số lượng',
                    data: Object.values(counts),
                    backgroundColor: [
                        'rgba(59, 130, 246, 0.8)', // Blue
                        'rgba(16, 185, 129, 0.8)', // Green
                        'rgba(245, 158, 11, 0.8)', // Orange
                        'rgba(239, 68, 68, 0.8)',  // Red
                        'rgba(139, 92, 246, 0.8)', // Purple
                    ],
                    borderRadius: 8,
                    borderSkipped: false as const,
                }],
            };

            return (
                <div className="h-[400px] w-full">
                    <Bar
                        data={data}
                        options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                                legend: { display: false },
                                tooltip: {
                                    backgroundColor: 'rgba(0,0,0,0.8)',
                                    padding: 12,
                                    titleFont: { size: 14 },
                                    bodyFont: { size: 14 }
                                }
                            },
                            scales: {
                                y: { beginAtZero: true, grid: { color: '#f3f4f6' } },
                                x: { grid: { display: false } }
                            }
                        }}
                    />
                </div>
            );
        } else if (currentQ.type === 'word_cloud') {
            const words = sessionState.responses.map(r => r.answer);
            const freqs: Record<string, number> = {};
            words.forEach(w => {
                // Case insensitive count
                const lower = w.toLowerCase();
                freqs[lower] = (freqs[lower] || 0) + 1;
            });

            // Sort by freq descending
            const sortedWords = Object.entries(freqs).sort((a, b) => b[1] - a[1]);

            // Color palette from user image
            const cloudColors = [
                '#0ea5e9', // Sky Blue
                '#10b981', // Emerald Green (Exercise)
                '#f59e0b', // Amber (Orange)
                '#8b5cf6', // Violet
                '#ec4899', // Pink
                '#ef4444', // Red
                '#6366f1', // Indigo
            ];

            return (
                <div className="flex flex-wrap gap-x-8 gap-y-4 items-center justify-center p-12 min-h-[400px] content-center">
                    {sortedWords.map(([word, freq], idx) => {
                        // Calculate size: Base 2rem + (freq * 1.5)rem, capped at 6rem
                        const size = Math.min(2 + (freq * 1.5), 6);
                        const color = cloudColors[idx % cloudColors.length];
                        // Random rotation between -5 and 5 degrees for organic look
                        const rotation = Math.random() * 10 - 5;

                        return (
                            <span
                                key={word}
                                className="font-black leading-none transition-all duration-700 ease-out animate-in zoom-in cursor-default hover:scale-110"
                                style={{
                                    fontSize: `${size}rem`,
                                    color: color,
                                    transform: `rotate(${rotation}deg)`,
                                    textShadow: '2px 2px 0px rgba(0,0,0,0.05)'
                                }}
                                title={`${freq} người chọn`}
                            >
                                {word.charAt(0).toUpperCase() + word.slice(1)}
                            </span>
                        );
                    })}
                    {sortedWords.length === 0 && (
                        <div className="text-gray-300 text-2xl font-light italic">Running word cloud...</div>
                    )}
                </div>
            );
        } else {
            // Short Answer - Hide Names
            return (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 auto-rows-max max-h-[500px] overflow-y-auto p-4 content-start">
                    {sessionState.responses.map((r, i) => (
                        <div key={i} className="bg-white p-6 rounded-xl shadow-sm border-l-4 border-l-indigo-400 hover:shadow-md transition-all animate-in slide-in-from-bottom-2">
                            <p className="text-gray-800 text-xl font-medium break-words leading-relaxed font-sans">
                                {r.answer}
                            </p>
                        </div>
                    ))}
                    {sessionState.responses.length === 0 && (
                        <div className="col-span-full text-center py-12 text-gray-400 text-lg italic">
                            Chờ câu trả lời từ học sinh...
                        </div>
                    )}
                </div>
            )
        }
    };

    // --- Main UI ---
    if (activeTab === 'create') {
        return (
            <div className="min-h-screen bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 flex items-center justify-center p-4">
                <div className="bg-white/95 backdrop-blur-xl p-8 rounded-3xl shadow-2xl max-w-md w-full text-center border border-white/20">
                    <div className="w-20 h-20 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-6 text-4xl shadow-inner">
                        🎓
                    </div>
                    <h1 className="text-4xl font-extrabold mb-3 text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">
                        Interactive Class
                    </h1>
                    <p className="text-gray-500 mb-8 text-lg">Tạo lớp học tương tác thời gian thực</p>

                    <button
                        onClick={startSession}
                        className="w-full bg-indigo-600 text-white py-4 rounded-2xl text-xl font-bold shadow-lg hover:bg-indigo-700 hover:shadow-indigo-500/30 transition-all transform hover:scale-[1.02] flex items-center justify-center gap-2 group"
                    >
                        <Play className="w-6 h-6 fill-current" />
                        Bắt đầu ngay
                    </button>

                    <div className="mt-8 pt-8 border-t border-gray-100">
                        <p className="text-xs text-gray-400 uppercase tracking-widest font-semibold mb-2">Được hỗ trợ bởi</p>
                        <div className="flex justify-center items-center gap-2 font-bold text-gray-600">
                            <span className="text-indigo-600">Vật Lí 365</span> Ebook Ecosystem
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col font-sans text-slate-800">
            {/* Header Toolbar */}
            <header className="bg-white border-b px-4 py-3 flex justify-between items-center shadow-sm z-20 relative">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => setSidebarOpen(!isSidebarOpen)}
                        className="p-2 hover:bg-gray-100 rounded-lg text-gray-500"
                    >
                        <Layout className="w-6 h-6" />
                    </button>
                    <div className="flex flex-col">
                        <h1 className="text-lg font-bold text-gray-800 leading-none">Interactive Session</h1>
                        <span className="text-xs text-gray-500">Live Control Center</span>
                    </div>
                </div>

                <div className="flex items-center gap-6">
                    {/* Sound Toggle */}
                    <button
                        onClick={() => setIsMuted(!isMuted)}
                        className={`p-2 rounded-full transition-all ${isMuted ? 'bg-gray-100 text-gray-400' : 'bg-indigo-100 text-indigo-600 shadow-sm'}`}
                        title={isMuted ? "Bật âm thanh" : "Tắt âm thanh"}
                    >
                        {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                    </button>

                    {/* Access Info Pill */}
                    <div
                        className="flex items-center bg-indigo-50 hover:bg-indigo-100 transition-colors rounded-full pl-1 pr-6 py-1 cursor-pointer border border-indigo-100"
                        onClick={() => setShowQR(true)}
                    >
                        <div className="bg-white p-2 rounded-full shadow-sm mr-3">
                            <QRCodeSVG value={`https://ebook.vatli365.vn/live/${sessionCode}`} size={24} />
                        </div>
                        <div className="flex flex-col items-start">
                            <span className="text-[10px] uppercase font-bold text-indigo-400 tracking-wider">Join Code</span>
                            <span className="text-xl font-black text-indigo-700 leading-none tracking-widest">{sessionCode}</span>
                        </div>
                    </div>

                    {/* Stats */}
                    <div className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-xl">
                        <Users className="w-5 h-5 text-gray-500" />
                        <span className="font-bold text-gray-700">{sessionState?.participants_count || 0}</span>
                    </div>

                    <button className="bg-red-50 text-red-600 px-4 py-2 rounded-lg font-medium hover:bg-red-100 transition-colors text-sm">
                        Kết thúc
                    </button>
                </div>
            </header>

            {/* Main Content */}
            <div className="flex-1 flex overflow-hidden">
                {/* Sidebar */}
                <div className={`${isSidebarOpen ? 'w-80' : 'w-0'} bg-white border-r transition-all duration-300 flex flex-col overflow-hidden`}>
                    <div className="p-4 border-b">
                        <h3 className="font-bold text-gray-700 mb-4 flex items-center gap-2">
                            <Settings className="w-5 h-5" />
                            Công cụ
                        </h3>

                        {/* File Upload */}
                        <div className="mb-6">
                            <label className="block w-full cursor-pointer bg-slate-50 border-2 border-dashed border-slate-300 rounded-xl p-4 text-center hover:bg-slate-100 hover:border-indigo-400 transition-all group">
                                <Upload className="w-6 h-6 mx-auto mb-2 text-slate-400 group-hover:text-indigo-500" />
                                <span className="text-sm font-medium text-slate-600 group-hover:text-indigo-600 block">Tải file câu hỏi (.json, .docx)</span>
                                <input type="file" accept=".json,.docx" onChange={handleFileUpload} className="hidden" />
                            </label>
                            <p className="text-xs text-gray-400 mt-2 text-center">
                                Format: [MC], [WC], [SA] + Text + Image
                            </p>
                        </div>

                        {/* Quick Question Tabs */}
                        <div>
                            <div className="flex bg-slate-100 p-1 rounded-lg mb-3">
                                {[
                                    { id: 'mcq', icon: BarChart2, label: 'MCQ' },
                                    { id: 'word_cloud', icon: Cloud, label: 'Cloud' },
                                    { id: 'short_answer', icon: Type, label: 'Text' }
                                ].map((tab) => (
                                    <button
                                        key={tab.id}
                                        onClick={() => setQuickType(tab.id as QuestionType)}
                                        className={`flex-1 flex flex-col items-center gap-1 py-2 rounded-md text-xs font-medium transition-all ${quickType === tab.id ? 'bg-white shadow text-indigo-600' : 'text-slate-500 hover:text-slate-700'}`}
                                    >
                                        <tab.icon className="w-4 h-4" />
                                        {tab.label}
                                    </button>
                                ))}
                            </div>

                            <textarea
                                value={quickText}
                                onChange={e => setQuickText(e.target.value)}
                                placeholder="Nhập câu hỏi nhanh..."
                                className="w-full p-3 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 mb-3"
                                rows={3}
                            />

                            {quickType === 'mcq' && (
                                <div className="space-y-2 mb-3 max-h-40 overflow-y-auto">
                                    {quickOptions.map((opt, i) => (
                                        <div key={i} className="flex gap-2">
                                            <span className="w-6 h-8 flex items-center justify-center bg-slate-200 text-xs font-bold rounded text-slate-600">
                                                {String.fromCharCode(65 + i)}
                                            </span>
                                            <input
                                                value={opt}
                                                onChange={e => {
                                                    const newOpts = [...quickOptions];
                                                    newOpts[i] = e.target.value;
                                                    setQuickOptions(newOpts);
                                                }}
                                                className="flex-1 p-1 bg-white border border-slate-200 rounded text-sm px-2"
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}

                            <button onClick={playQuickQuestion} className="w-full btn-primary py-2.5 rounded-lg text-sm font-bold shadow-md hover:shadow-lg transition-all">
                                Phát ngay ⚡
                            </button>
                        </div>
                    </div>

                    {/* Question List from File */}
                    <div className="flex-1 overflow-y-auto p-4">
                        <h4 className="font-bold text-gray-500 text-xs uppercase tracking-wider mb-3">Danh sách câu hỏi ({questions.length})</h4>
                        <div className="space-y-2">
                            {questions.map((q, idx) => (
                                <div
                                    key={idx}
                                    onClick={() => {
                                        setCurrentQuestionIndex(idx);
                                        // Optional: Auto play? Or just select? Let's just select for now.
                                        playQuestion(q);
                                    }}
                                    className={`p-3 rounded-lg border cursor-pointer transition-all hover:bg-indigo-50 ${currentQuestionIndex === idx ? 'bg-indigo-50 border-indigo-500 shadow-sm' : 'bg-white border-transparent hover:border-indigo-200'}`}
                                >
                                    <div className="flex justify-between items-start mb-1">
                                        <span className={`text-[10px] px-2 py-0.5 rounded font-bold uppercase ${q.type === 'mcq' ? 'bg-blue-100 text-blue-600' : q.type === 'word_cloud' ? 'bg-green-100 text-green-600' : 'bg-yellow-100 text-yellow-600'}`}>
                                            {q.type.replace('_', ' ')}
                                        </span>
                                    </div>
                                    <p className="text-sm text-gray-800 line-clamp-2 font-medium">{renderLatexText(q.text)}</p>
                                </div>
                            ))}
                            {questions.length === 0 && (
                                <div className="text-center py-8 text-gray-400 text-xs">
                                    Chưa có câu hỏi nào.
                                    <br />Hãy tải file hoặc tạo nhanh.
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Main View */}
                <div className="flex-1 bg-slate-100 p-8 flex flex-col items-center justify-center relative overflow-y-auto">
                    {/* Background Pattern */}
                    <div className="absolute inset-0 opacity-10 pointer-events-none" style={{ backgroundImage: 'radial-gradient(#cbd5e1 1.5px, transparent 1.5px)', backgroundSize: '24px 24px' }}></div>

                    {sessionState?.current_question ? (
                        <div className="w-full max-w-5xl z-10 animate-in fade-in duration-500">
                            {/* Question Card */}
                            <div className="bg-white rounded-3xl shadow-xl overflow-hidden mb-8 border border-white/50">
                                <div className="bg-indigo-600 px-8 py-6 flex items-start gap-4 text-white">
                                    <div className="bg-white/20 p-3 rounded-2xl backdrop-blur-sm mt-1">
                                        {sessionState.current_question.type === 'mcq' && <BarChart2 className="w-8 h-8" />}
                                        {sessionState.current_question.type === 'word_cloud' && <Cloud className="w-8 h-8" />}
                                        {sessionState.current_question.type === 'short_answer' && <MessageSquare className="w-8 h-8" />}
                                    </div>
                                    <div className="flex-1">
                                        <div className="text-3xl font-bold leading-tight flex flex-col gap-4">
                                            <div>{renderLatexText((sessionState.current_question.data as any).text)}</div>
                                            {(sessionState.current_question.data as any).image && (
                                                <img
                                                    src={(sessionState.current_question.data as any).image}
                                                    alt="Question Illustration"
                                                    className="max-h-64 object-contain rounded-lg bg-white p-2 w-auto self-start shadow-md"
                                                />
                                            )}
                                        </div>
                                    </div>
                                    <div className="bg-white/10 px-4 py-2 rounded-lg font-mono text-xl font-bold backdrop-blur-sm whitespace-nowrap">
                                        {sessionState.responses.length} Trả lời
                                    </div>
                                </div>

                                {/* Visualization Area */}
                                <div className="p-8 bg-white min-h-[400px] flex items-center justify-center">
                                    {renderChart()}
                                </div>
                            </div>

                            {/* Bottom Controls (Optional) */}
                            {/* <div className="flex justify-center gap-4">
                                 <button className="bg-white px-6 py-3 rounded-xl shadow-lg font-bold text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                                     <StopCircle className="w-5 h-5 text-red-500" /> Dừng nhận câu trả lời
                                 </button>
                             </div> */}
                        </div>
                    ) : (
                        <div className="text-center text-gray-400 z-10 max-w-md">
                            <div className="bg-white p-8 rounded-full inline-block mb-6 shadow-xl animate-bounce">
                                <Play className="w-12 h-12 text-indigo-500 ml-1" />
                            </div>
                            <h2 className="text-2xl font-bold text-gray-600 mb-2">Sẵn sàng!</h2>
                            <p>Chọn câu hỏi từ danh sách bên trái hoặc tạo nhanh để bắt đầu.</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Modals */}
            <QRModal
                code={sessionCode || ''}
                isOpen={showQR}
                onClose={() => setShowQR(false)}
            />
        </div>
    );
};

export default TeacherSession;
